import { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { ArrowDownUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { formatVND, formatCompact } from '@/lib/utils';
import { transferMoney } from '../../../services/transferService';
import { useData } from '../../store/data';
import { parseAmount, parseMoneyOrNull } from './formSchema';

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, '');

/** Ví nguồn mặc định: "VietinBank" (khớp đúng tên, bỏ qua dấu/hoa thường); không có thì để trống. */
export const defaultTransferSource = (wallets: Array<{ id: string; name?: string }>): string =>
  wallets.find(w => norm(w.name ?? '') === 'vietinbank')?.id ?? '';

/** Tab "Chuyển" của Ghi nhanh: chuyển giữa hai ví bất kỳ (không gồm thẻ tín dụng). Dùng lại transferMoney của sổ cái. */
export default function TransferPanel({ onDone }: { onDone: () => void }) {
  const sources = useData(s => s.data.sources);
  const wallets = useMemo(() => sources.filter(s => s.status !== 'inactive' && s.type !== 'credit'), [sources]);
  const [from, setFrom] = useState('');
  const [touched, setTouched] = useState(false);
  const [to, setTo] = useState('');
  const [amountText, setAmountText] = useState('');
  const [feeText, setFeeText] = useState('');
  const [date, setDate] = useState(isoDate(new Date()));
  const [note, setNote] = useState('');
  const [err, setErr] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Ví nguồn mặc định là VietinBank, trừ khi thầy đã tự chọn ví khác
  useEffect(() => { if (!touched && !from) setFrom(defaultTransferSource(wallets)); }, [wallets, touched, from]);

  const amount = parseAmount(amountText);
  const fee = parseMoneyOrNull(feeText) ?? 0;
  const src = wallets.find(w => w.id === from);
  const label = (w: typeof wallets[number]) => `${w.name} · ${formatCompact(Number(w.balance) || 0)}`;
  const swap = () => { setTouched(true); setFrom(to); setTo(from); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v: Record<string, string> = {};
    if (!from) v.from = 'Chọn ví nguồn';
    if (!to) v.to = 'Chọn ví đích';
    if (from && from === to) v.to = 'Ví đích phải khác ví nguồn';
    if (amount <= 0) v.amount = 'Nhập số tiền lớn hơn 0 (vd: 5tr)';
    if (feeText.trim() && parseMoneyOrNull(feeText) === null) v.fee = 'Phí không hợp lệ';
    if (src && src.type !== 'cash' && amount + fee > (Number(src.balance) || 0)) v.amount = `Số dư ${src.name} chỉ còn ${formatVND(Number(src.balance) || 0)} (gồm phí).`;
    setErr(v);
    if (Object.keys(v).length) return;
    setServerError(null); setBusy(true);
    try {
      const r = await transferMoney(getAuth().currentUser!.uid, { fromSourceId: from, toSourceId: to, amount, fee, transferDate: date, note });
      if (!r.success) { setServerError('Không chuyển được.'); return; }
      toast.success(`Đã chuyển ${formatVND(amount)} từ ${src?.name} sang ${wallets.find(w => w.id === to)?.name}`);
      onDone();
    } catch (ex) {
      setServerError((ex as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className="space-y-1.5">
        <Label htmlFor="t-amount">Số tiền</Label>
        <Input id="t-amount" inputMode="text" autoComplete="off" autoFocus placeholder="vd: 5tr, 20tr" className="num text-lg" aria-invalid={!!err.amount} value={amountText} onChange={e => setAmountText(e.target.value)} />
        <p className={err.amount ? 'num text-xs text-bad' : 'num text-xs text-muted-foreground'}>{err.amount ?? (amount > 0 ? `= ${formatVND(amount)}` : 'Gõ tắt: 50k · 1.5tr · 2 tỷ')}</p>
      </div>

      <div className="space-y-1.5">
        <Label>Từ ví</Label>
        <Select value={from} onValueChange={(v) => { setTouched(true); setFrom(v); if (v === to) setTo(''); }}>
          <SelectTrigger aria-label="Từ ví" aria-invalid={!!err.from}><SelectValue placeholder="Chọn ví nguồn" /></SelectTrigger>
          <SelectContent>{wallets.map(w => <SelectItem key={w.id} value={w.id}>{label(w)}</SelectItem>)}</SelectContent>
        </Select>
        {err.from && <p className="text-xs text-bad">{err.from}</p>}
      </div>

      <div className="flex justify-center">
        <Button type="button" variant="ghost" size="sm" onClick={swap} disabled={!from && !to}><ArrowDownUp size={14} aria-hidden /> Đảo chiều</Button>
      </div>

      <div className="space-y-1.5">
        <Label>Đến ví</Label>
        <Select value={to} onValueChange={setTo}>
          <SelectTrigger aria-label="Đến ví" aria-invalid={!!err.to}><SelectValue placeholder="Chọn ví đích" /></SelectTrigger>
          <SelectContent>{wallets.filter(w => w.id !== from).map(w => <SelectItem key={w.id} value={w.id}>{label(w)}</SelectItem>)}</SelectContent>
        </Select>
        {err.to && <p className="text-xs text-bad">{err.to}</p>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="t-fee">Phí chuyển</Label>
          <Input id="t-fee" inputMode="text" autoComplete="off" placeholder="Tuỳ chọn" className="num" aria-invalid={!!err.fee} value={feeText} onChange={e => setFeeText(e.target.value)} />
          {err.fee && <p className="text-xs text-bad">{err.fee}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="t-date">Ngày</Label>
          <Input id="t-date" type="date" className="num" value={date} onChange={e => setDate(e.target.value)} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="t-note">Ghi chú</Label>
        <Input id="t-note" autoComplete="off" placeholder="Tuỳ chọn" value={note} onChange={e => setNote(e.target.value)} />
      </div>

      <p className="text-xs text-muted-foreground">Chuyển giữa ví của bạn không phải chi tiêu. Phí (nếu có) tự ghi thành chi phí tài chính. Thẻ tín dụng trả ở Tài sản & Nợ › Trả thẻ.</p>
      {serverError && <p role="alert" className="rounded-md bg-bad-soft px-3 py-2 text-sm text-bad">{serverError}</p>}
      <Button type="submit" className="w-full" disabled={busy}>{busy ? 'Đang chuyển…' : 'Chuyển'}</Button>
    </form>
  );
}
